/**
 * Reset Password Page
 *
 * Implements the password reset confirmation flow using Supabase Auth.
 * Users are redirected here after clicking a password reset link in their email.
 * The reset token is passed as a URL fragment and handled by Supabase client automatically.
 *
 * Features:
 * - Form validation with react-hook-form and Zod
 * - Password strength requirements
 * - Error handling for invalid/expired tokens
 * - Loading state during reset
 * - Auto-redirect to login on success
 *
 * See: docs/v2/03-auth-and-security.md
 *
 * ## Route note (GitHub issue #494 — "E2E: Authentication — login,
 * signup, OTP, password reset, session management")
 *
 * The ticket's "Routes Covered" list says `/reset-password`; this page is
 * actually mounted at `/auth/reset-password` (see `app/router.tsx`) —
 * matching `requestPasswordReset()`'s `redirectTo:
 * ${window.location.origin}/auth/reset-password` in
 * `@/lib/auth/supabaseAuth.ts`, which this page must stay in sync with.
 * `e2e/password-reset-auth-flow.spec.ts` exercises the real
 * `/auth/reset-password` path rather than renaming the route to match the
 * ticket's (inconsistent-with-the-rest-of-the-codebase) text.
 *
 * Also added here: a `sonner` toast on successful/failed reset, alongside
 * the existing inline banners (same rationale as the sibling auth pages).
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { resetPasswordSchema, type ResetPasswordFormData } from '@/lib/validation/auth';
import { resetPassword } from '@/lib/auth/supabaseAuth';
import { getCurrentSession } from '@/lib/auth/supabaseAuth';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  /**
   * Check if the reset token is valid by verifying session state
   * The Supabase client automatically parses the recovery token from the URL fragment
   */
  useEffect(() => {
    async function checkToken() {
      try {
        const session = await getCurrentSession();
        // If we don't have a session, the token might be invalid or expired
        if (!session) {
          setIsTokenValid(false);
          setApiError(
            'Invalid or expired reset link. Please request a new password reset.'
          );
        }
      } catch (error) {
        console.error('[ResetPassword] Failed to check token:', error);
        setIsTokenValid(false);
      } finally {
        setIsCheckingToken(false);
      }
    }

    checkToken();
  }, []);

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsSubmitting(true);
    setApiError(null);

    const { error } = await resetPassword(data.password);

    if (error) {
      // Check if this is a "not authenticated" error, which means invalid token
      if (error.message.includes('not authenticated') || error.message.includes('Invalid')) {
        const message = 'Invalid or expired reset link. Please request a new password reset.';
        setApiError(message);
        toast.error(message);
        setIsTokenValid(false);
      } else {
        setApiError(error.message);
        toast.error(error.message);
      }
      setIsSubmitting(false);
      return;
    }

    // Show success message
    setResetSuccess(true);
    toast.success('Password reset successfully');

    // Redirect to login after a brief delay
    setTimeout(() => {
      navigate('/login', {
        state: {
          message: 'Password reset successfully. Please sign in with your new password.',
        },
      });
    }, 2000);
  };

  if (isCheckingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-foreground">
              Verifying reset link...
            </h1>
            <p className="text-sm text-muted-foreground mt-4">
              Please wait while we validate your reset token.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isTokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">
              Invalid Reset Link
            </h1>
            <p className="text-muted-foreground mt-4">
              {apiError || 'Your password reset link is invalid or has expired.'}
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Please request a new password reset to continue.
            </p>
            <div className="mt-6 space-y-2">
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 transition-colors"
              >
                Request Password Reset
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full px-4 py-2 bg-secondary text-secondary-foreground font-medium rounded-md hover:bg-secondary/90 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-green-600">
              Password Reset Successfully
            </h1>
            <p className="text-muted-foreground mt-4">
              Your password has been reset. You can now sign in with your new password.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Redirecting to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
        <div className="text-center">
          <Link to="/" data-testid="reset-password-logo">
            <h1 className="text-3xl font-bold">Hero Hire AI</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Set a new password</p>
        </div>

        {apiError && (
          <div className="p-4 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('password')}
                className="w-full px-3 py-2 pr-10 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-password-visibility"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-destructive">
                {errors.password.message}
              </p>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Must be at least 8 characters with uppercase, lowercase, and
              number
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium mb-2"
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('confirmPassword')}
                className="w-full px-3 py-2 pr-10 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-confirm-password-visibility"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-destructive">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        {/* Link back to login */}
        <div className="text-center text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-primary hover:underline font-medium"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
